// strafe.games — shared client auth helper (framework-agnostic, zero deps).
//
// CANONICAL COPY. Mirrored verbatim into every game's src/ (games share no
// build). Only edit the canonical copy here, then re-sync.
//
// This is a thin, dormant-safe wrapper over the cookie-auth backend. The
// session itself lives in an HttpOnly cookie set by the Workers backend; this
// module NEVER stores a token — it only reads the decoded session blob from
// GET /api/me (sent with `credentials: 'include'`) and offers redirect-based
// sign-in + a sign-out POST.
//
// DORMANT: when `VITE_FEATURE_AUTH !== 'true'` every method is inert —
// getSession() returns null without ever touching the network, signInUrl()
// still composes a URL (harmless), signOut() only clears the local cursor.
//
// SIGN-IN MODEL: a full-page redirect to `${apiBase}/api/auth/sign-in/:provider`
// kicks off the OAuth round-trip; the backend 302s back with no token in the
// URL (the JWT stays off the address bar / Referer / history). The frontend
// detects the new session purely via getSession() on the next load — there is
// no `?auth=...` exchange. The `onSignIn` cursor hook lets a host fire a
// one-time action (e.g. local→server migration) the first time a session
// appears after being absent.

export type Provider = 'discord' | 'google' | 'github' | 'password';

export interface AuthSession {
  userId: string;
  email: string;
  displayName: string;
  provider: Provider;
  avatarUrl: string | null;
  expiresAt: number;
}

/** Result of a register/login attempt. Never thrown — always returned. */
export type AuthResult = { ok: true } | { ok: false; error: string };

const PROVIDERS: ReadonlySet<Provider> = new Set<Provider>([
  'discord',
  'google',
  'github',
  'password',
]);

/** Only the OAuth providers are valid full-page-redirect targets. */
const REDIRECT_PROVIDERS: ReadonlySet<Provider> = new Set<Provider>([
  'discord',
  'google',
  'github',
]);

const CURSOR_KEY = 'strafe.auth.lastKnownUserId';

// ---- env / base resolution (mirrors platform.ts) -----------------------------

/** AUTH master gate. With it off, this whole module is dormant. */
export function isAuthEnabled(): boolean {
  return import.meta.env.VITE_FEATURE_AUTH === 'true';
}

/** Backend origin, trailing slash trimmed. Same resolution as platform.ts. */
export function getApiBase(): string {
  // PROD defaults to the API custom domain api.strafe.games (same strafe.games
  // eTLD+1 as the frontend -> same-site: first-party cookie + honest client IP).
  // Override with VITE_API_BASE_URL only for a custom backend.
  const raw =
    import.meta.env.VITE_API_BASE_URL
    ?? (import.meta.env.DEV ? 'http://localhost:8787' : 'https://api.strafe.games');
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

// ---- session parse (defensive; never trusts the wire) ------------------------

function parseSession(raw: unknown): AuthSession | null {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.userId !== 'string' || o.userId.length === 0) return null;
  if (typeof o.email !== 'string') return null;
  if (typeof o.displayName !== 'string') return null;
  if (typeof o.provider !== 'string' || !PROVIDERS.has(o.provider as Provider)) return null;
  if (typeof o.expiresAt !== 'number' || !Number.isFinite(o.expiresAt)) return null;
  const avatarUrl = typeof o.avatarUrl === 'string' ? o.avatarUrl : null;
  return {
    userId: o.userId,
    email: o.email,
    displayName: o.displayName,
    provider: o.provider as Provider,
    avatarUrl,
    expiresAt: o.expiresAt,
  };
}

// ---- cursor (last-known userId; drives the onSignIn hook) ---------------------

function readCursor(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(CURSOR_KEY);
  } catch {
    return null;
  }
}

function writeCursor(value: string | null): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (value === null) localStorage.removeItem(CURSOR_KEY);
    else localStorage.setItem(CURSOR_KEY, value);
  } catch {
    /* private mode / quota — non-fatal */
  }
}

// ---- public API --------------------------------------------------------------

/** Hit GET /api/me. Returns the validated session, or null on disabled / 401 /
 *  throw / parse failure / invalid shape. NEVER throws. */
export async function getSession(): Promise<AuthSession | null> {
  if (!isAuthEnabled()) return null;
  if (typeof fetch === 'undefined') return null;
  let res: Response;
  try {
    res = await fetch(`${getApiBase()}/api/me`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch {
    return null;
  }
  if (!res.ok) return null;
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return null;
  }
  const session = parseSession(body);
  if (session !== null) maybeFireSignIn(session); // host hook + cursor advance
  return session;
}

/** Full-page redirect URL to start the OAuth round-trip for a provider. */
export function signInUrl(provider: Provider): string {
  return `${getApiBase()}/api/auth/sign-in/${provider}`;
}

/** Navigate the browser to the provider sign-in. No-op if no window. Only the
 *  OAuth providers are redirect targets — `password` uses register/login. */
export function signIn(provider: Provider): void {
  if (!REDIRECT_PROVIDERS.has(provider)) return;
  if (typeof window === 'undefined' || typeof window.location === 'undefined') return;
  window.location.href = signInUrl(provider);
}

// ---- email + password credentials (mutating POSTs, no prior session needed) --

/** Map a credentials response into an AuthResult, then (on success) pull the
 *  fresh session so subscribers + the onSignIn migration hook fire. NEVER
 *  throws. On a non-ok response the server's `error` code is surfaced verbatim
 *  (the chip maps it to a friendly message); a network/parse failure becomes a
 *  generic `'failed'`. */
async function postCredentials(
  path: '/api/auth/register' | '/api/auth/login',
  email: string,
  password: string,
  turnstileToken?: string,
): Promise<AuthResult> {
  if (!isAuthEnabled()) return { ok: false, error: 'auth_disabled' };
  if (typeof fetch === 'undefined') return { ok: false, error: 'failed' };

  // Include the Turnstile token only when it's a non-empty string (dormant-safe:
  // with no site key configured the chip passes nothing and the body is identical
  // to today). login() never passes a token, so its body is unchanged.
  const payload: { email: string; password: string; turnstileToken?: string } = { email, password };
  if (typeof turnstileToken === 'string' && turnstileToken.length > 0) {
    payload.turnstileToken = turnstileToken;
  }

  let res: Response;
  try {
    res = await fetch(`${getApiBase()}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    return { ok: false, error: 'failed' };
  }

  if (res.ok) {
    // Session cookie is now set — refresh so subscribers (the chip) re-render to
    // signed-in and the one-time onSignIn migration hook can fire.
    await getSession();
    // platform.ts only enters signedIn mode + runs migration during init() at
    // boot; without a reload the user stays in anon mode until next load. Mirror
    // signOut(): a guarded full-page reload re-inits the SDK signedIn (and runs
    // maybeMigrate) so the in-session login takes effect immediately.
    if (isAuthEnabled() && typeof window !== 'undefined' && typeof window.location !== 'undefined') {
      window.location.reload();
    }
    return { ok: true };
  }

  // Surface the server's error code so the UI can pick a friendly message.
  let error = 'failed';
  try {
    const body = (await res.json()) as unknown;
    if (body !== null && typeof body === 'object' && !Array.isArray(body)) {
      const e = (body as Record<string, unknown>).error;
      if (typeof e === 'string' && e.length > 0) error = e;
    }
  } catch {
    /* fall through to status-based mapping below */
  }
  // Status-based fallbacks when the body had no usable `error` field.
  if (error === 'failed') {
    if (res.status === 401) error = 'invalid_credentials';
    else if (res.status === 429) error = 'rate_limited';
  }
  return { ok: false, error };
}

/** Register an email/password account. On 200 the backend auto-logs-in (sets a
 *  session cookie) and we pull GET /api/me. Returns `{ok:false,error}` for
 *  invalid_email / weak_password / invalid_body / email_taken, or
 *  `'auth_disabled'` when dormant. NEVER throws. `turnstileToken`, when a
 *  non-empty string, is forwarded in the POST body for bot protection (dormant
 *  when no Turnstile site key is configured, so the wire is unchanged). */
export async function register(
  email: string,
  password: string,
  turnstileToken?: string,
): Promise<AuthResult> {
  return postCredentials('/api/auth/register', email, password, turnstileToken);
}

/** Sign in with email/password. On 200 a session cookie is set and we pull GET
 *  /api/me. Returns `{ok:false,error}` for invalid_credentials / rate_limited /
 *  invalid_body, or `'auth_disabled'` when dormant. NEVER throws. */
export async function login(email: string, password: string): Promise<AuthResult> {
  return postCredentials('/api/auth/login', email, password);
}

/** POST sign-out (swallowing errors), clear the local cursor, reload. When
 *  AUTH is disabled, only clears the cursor (no network, no reload). */
export async function signOut(): Promise<void> {
  if (isAuthEnabled() && typeof fetch !== 'undefined') {
    try {
      await fetch(`${getApiBase()}/api/auth/sign-out`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      /* network down / CORS — local clear still proceeds */
    }
  }
  writeCursor(null);
  notify(null);
  if (isAuthEnabled() && typeof window !== 'undefined' && typeof window.location !== 'undefined') {
    window.location.reload();
  }
}

// ---- pub/sub -----------------------------------------------------------------

type Listener = (session: AuthSession | null) => void;
const listeners = new Set<Listener>();

function notify(session: AuthSession | null): void {
  for (const l of listeners) l(session);
}

/** Subscribe to session changes (sign-in detected via getSession, sign-out).
 *  Returns an unsubscribe. */
export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

// ---- onSignIn cursor hook ----------------------------------------------------

const signInHooks = new Set<(session: AuthSession) => void>();

/** Fires `cb` the first time a session appears whose userId differs from the
 *  last-known cursor (empty cursor OR a different user). Lets a host trigger a
 *  one-time-per-account action — e.g. local→server economy migration — without
 *  re-firing on every page load for the same already-known user. Returns an
 *  unsubscribe. */
export function onSignIn(cb: (session: AuthSession) => void): () => void {
  signInHooks.add(cb);
  return () => {
    signInHooks.delete(cb);
  };
}

function maybeFireSignIn(session: AuthSession): void {
  const prior = readCursor();
  const isNew = prior === null || prior.length === 0 || prior !== session.userId;
  // Always advance the cursor + broadcast the current session to subscribers.
  writeCursor(session.userId);
  notify(session);
  if (isNew) {
    for (const cb of signInHooks) {
      try {
        cb(session);
      } catch {
        /* a faulty host hook must not break auth */
      }
    }
  }
}
